import React from 'react';
import { Bar } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ChartOptions
} from 'chart.js';

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
);

const DriverSpendingShift = () => {
  const defaultData = {
    title: "Drivers of Spending Shift",
    chartSection: {
      title: "Top Reasons Spending on Dog Food Increased (Past 12 Months)",
      chartData: [
        { label: "Cost of dog food increased overall", value: 39, color: "#86BC25" },
        { label: "Switching to higher quality food", value: 20, color: "#86BC25" },
        { label: "Increase in supply problems/cost", value: 15, color: "#86BC25" },
        { label: "More or new dogs added", value: 12, color: "#86BC25" },
        { label: "Change in dog's dietary needs", value: 14, color: "#86BC25" }
      ]
    },
    infoBoxes: [
      {
        title: "Health needs are an important driver",
        bgColor: "bg-green-50",
        borderColor: "border-lime-400",
        points: [
          "Nearly 15% cite dietary changes, a significant chunk among households with senior dogs or health conditions.",
          "All costing more."
        ],
        subSection: {
          title: "Most-important areas:",
          items: [
            "Sensitive stomach formulas",
            "Weight management foods"
          ]
        }
      },
      {
        title: "Market expansion encourages trial—and higher spend",
        bgColor: "bg-green-50",
        borderColor: "border-lime-400",
        points: [
          "Between the variety expansion and desire to try new options (12–14%), persuasive are spending more:",
          "New brands",
          "New formats",
          "Functional enhancements"
        ],
        subPoint: "This \"upgrade pressure\" is a part but not driver of rising cost."
      }
    ],
    rightSection: {
      boxes: [
        {
          title: "Spending increases are overwhelmingly driven by inflation",
          bgColor: "bg-yellow-50",
          borderColor: "border-yellow-400",
          points: [
            "The primary driver is structural: 65% say dog food costs have risen.",
            "This reflects exposure to price inflation in the category, driven by ingredient and operational costs."
          ]
        },
        {
          title: "Premiumization contributes significantly",
          bgColor: "bg-yellow-50",
          borderColor: "border-yellow-400",
          mainPoints: [
            "1 in 5 owners switched to higher-quality dog food, directly increasing their volume.",
            "Key catalysts:"
          ],
          columns: [
            {
              items: [
                "Breeder pressure",
                "Vet/veterinary views",
                "Concern over recalls"
              ]
            },
            {
              items: [
                "Ingredient benefits",
                "Subscription services",
                "Lifestyle/humanization (fresh, raw, no grains, made on a small scale)"
              ]
            }
          ]
        },
        {
          title: "Why decreases matter (but do not define the market)",
          bgColor: "bg-yellow-50",
          borderColor: "border-yellow-400",
          points: [
            "A minority cut spending (10–15%):",
            "Seeking deals",
            "Using lifestyle pressure",
            "Trading to value"
          ],
          note: "This group is small and highly price-sensitive, not representative of the category's broader direction."
        }
      ]
    }
  };

  const componentData =  defaultData;

  const chartData = {
    labels: componentData.chartSection.chartData.map(item => item.label),
    datasets: [
      {
        data: componentData.chartSection.chartData.map(item => item.value),
        backgroundColor: componentData.chartSection.chartData.map(item => item.color),
        borderRadius: 4,
        barThickness: 30,
      }
    ]
  };

  const chartOptions:ChartOptions<"bar"> = {
    indexAxis: 'y',
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false
      },
      tooltip: {
        callbacks: {
          label: function(context:any) {
            return context.parsed.x + '%';
          }
        }
      }
    },
    scales: {
      x: {
        beginAtZero: true,
        max: 50,
        ticks: {
          callback: function(value:any) {
            return value + '%';
          },
          stepSize: 10
        },
        grid: {
          display: true,
          color: '#e5e7eb'
        }
      },
      y: {
        grid: {
          display: false
        },
        ticks: {
          font: {
            size: 11
          },
          color: '#374151'
        }
      }
    }
  };

  return (
    <div className="flex flex-col h-full items-center w-full">
      <h1 className="relative inline-block text-white text-center font-extrabold 
  max-w-4xl text-2xl lg:text-4xl font-nunito mb-4 
  bg-gradient-to-r from-[#92D050] to-[#8CC54F] 
  px-6 py-2
  before:content-[''] before:absolute before:left-0 before:top-1/2 
  before:-translate-y-1/2 before:-translate-x-full
  before:border-y-[16px] before:border-r-[16px] before:border-y-transparent 
  before:border-r-[#92D050]

  after:content-[''] after:absolute after:right-0 after:top-1/2 
  after:-translate-y-1/2 after:translate-x-full
  after:border-y-[16px] after:border-l-[16px] after:border-y-transparent 
  after:border-l-[#8CC54F]">{componentData.title}</h1>
     

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
        {/* Left Column */}
        <div className="space-y-1">
          {/* Chart Section */}
          <div className='mb-4'>
            <h2 className="text-base text-center font-bold mb-4 text-gray-900">
              {componentData.chartSection.title}
            </h2>
            <div className='h-[250px]'>
              <Bar data={chartData} options={chartOptions} />
            </div>
          </div>

       
          {/* Info Boxes */}
          {componentData.infoBoxes.map((box, index) => (
            <div
              key={index}
              className={`border-2 ${box.borderColor} p-2 rounded-r-lg`}
            >
              <h3 className="font-bold text-gray-900 mb-3 text-base">
                {box.title}
              </h3>
              <ul className="space-y-1">
                {box.points.map((point, idx) => (
                  <li key={idx} className="text-sm text-gray-700 flex items-start">
                    <span className="mr-2">•</span>
                    <span>{point}</span>
                  </li>
                ))}
              </ul>
              {box.subSection && (
                <div className="mt-3">
                  <p className="text-sm font-semibold text-gray-800 mb-1">
                    {box.subSection.title}
                  </p>
                  <ul className="ml-4 space-y-1">
                    {box.subSection.items.map((item, idx) => (
                      <li key={idx} className="text-sm text-gray-700">○ {item}</li>
                    ))}
                  </ul>
                </div>
              )}
              {box.subPoint && (
                <p className="text-sm text-gray-700 mt-3 ml-2">• {box.subPoint}</p>
              )}
            </div>
          ))}
         
        </div>

        {/* Right Column */}
        <div className="space-y-4 border-2 border-lime-400 p-4 rounded-xl">
          {componentData.rightSection.boxes.map((box, index) => (
            <div
              key={index}
              className={``}
            >
              <h3 className="font-bold text-gray-900 mb-3 text-base">
                {box.title}
              </h3>
              
              {box.points && (
                <ul className="space-y-2">
                  {box.points.map((point, idx) => (
                    <li key={idx} className="text-sm text-gray-700 flex items-start">
                      <span className="mr-2">•</span>
                      <span>{point}</span>
                    </li>
                  ))}
                </ul>
              )}

              {box.mainPoints && (
                <div className="space-y-3">
                  {box.mainPoints.map((point, idx) => (
                    <p key={idx} className="text-sm text-gray-700 flex items-start">
                      <span className="mr-2">•</span>
                      <span>{point}</span>
                    </p>
                  ))}
                </div>
              )}

              {box.columns && (
                <div className="grid grid-cols-2 gap-4 mt-3">
                  {box.columns.map((column, colIdx) => (
                    <ul key={colIdx} className="space-y-1">
                      {column.items.map((item, itemIdx) => (
                        <li key={itemIdx} className="text-sm text-gray-700 flex items-start">
                          <span className="mr-2">○</span>
                          <span>{item}</span>
                        </li>
                      ))}
                    </ul>
                  ))}
                </div>
              )}

              {box.note && (
                <p className="text-sm text-gray-700 mt-3 italic">
                  {box.note}
                </p>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default DriverSpendingShift;